import { useState, useEffect } from 'react';
import { ArrowRight, Download, CheckCircle } from 'lucide-react';
import { getCenters, importCenterConfig } from '../../lib/db';
import { Center } from '../../types';

export function ImportConfigManager() {
  const [centers, setCenters] = useState<Center[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [sourceCenterId, setSourceCenterId] = useState('');
  const [targetCenterId, setTargetCenterId] = useState('');
  const [options, setOptions] = useState({
    levels: true,
    instruments: true,
    groupSubjects: true,
    evaluationCriteria: true,
  });
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCenters().then(setCenters).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleImport = async () => {
    if (!sourceCenterId || !targetCenterId) {
      setError('Selecciona ambos centros');
      return;
    }
    if (sourceCenterId === targetCenterId) {
      setError('Los centros origen y destino deben ser diferentes');
      return;
    }
    if (!Object.values(options).some(Boolean)) {
      setError('Selecciona al menos una opción a importar');
      return;
    }

    try {
      setError(null);
      setResult(null);
      setImporting(true);
      await importCenterConfig(sourceCenterId, targetCenterId, options);
      setResult('Configuración importada correctamente');
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-400">Cargando centros...</div>;

  const optionItems = [
    { key: 'levels' as const, label: 'Niveles' },
    { key: 'instruments' as const, label: 'Instrumentos' },
    { key: 'groupSubjects' as const, label: 'Asignaturas Grupales' },
    { key: 'evaluationCriteria' as const, label: 'Criterios de Evaluación' },
  ];

  return (
    <div>
      <h2 className="mb-2 text-lg font-semibold text-gray-900">Importar Configuración</h2>
      <p className="mb-6 text-sm text-slate-500">
        Copia la configuración de un centro a otro. Solo se copiarán elementos que no existan ya en el centro destino.
      </p>

      {error && (
        <div className="p-3 mb-4 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}
      {result && (
        <div className="flex items-center gap-2 p-3 mb-4 text-sm text-green-700 bg-green-50 rounded-lg">
          <CheckCircle className="w-4 h-4" />
          {result}
        </div>
      )}

      <div className="space-y-6">
        {/* Center selection */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block mb-1 text-sm font-medium text-slate-700">Centro Origen</label>
            <select
              value={sourceCenterId}
              onChange={e => { setSourceCenterId(e.target.value); setResult(null); }}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Seleccionar...</option>
              {centers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <ArrowRight className="w-5 h-5 mb-2 text-slate-400 shrink-0" />

          <div className="flex-1">
            <label className="block mb-1 text-sm font-medium text-slate-700">Centro Destino</label>
            <select
              value={targetCenterId}
              onChange={e => { setTargetCenterId(e.target.value); setResult(null); }}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Seleccionar...</option>
              {centers.filter(c => c.id !== sourceCenterId).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Options checkboxes */}
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">Datos a importar</label>
          <div className="grid grid-cols-2 gap-2">
            {optionItems.map(item => (
              <label
                key={item.key}
                className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                  options[item.key]
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={options[item.key]}
                  onChange={e => setOptions({ ...options, [item.key]: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Import button */}
        <button
          onClick={handleImport}
          disabled={importing || !sourceCenterId || !targetCenterId}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          {importing ? 'Importando...' : 'Importar Configuración'}
        </button>
      </div>
    </div>
  );
}
